import { useEffect, useId, useState } from "react";
import styled from "@emotion/styled";
import { colors as themeColors } from "src/theme";
import { InputLabel } from "src/components";
import { ValidationErrorStyle } from "src/common";
import { errorIdFor, focusRing, visuallyHidden, onEnterKey } from "src/common/style/a11y.style";

const Container = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const TextContainer = styled.div({
  flex: 1,
});

const ToggleContainer = styled.label({
  position: 'relative',
  width: '44px',
  height: '24px',
  flexShrink: 0,
});

const HiddenInput = styled.input({
  ...visuallyHidden,
  '&:focus-visible + span': focusRing,
});

const ToggleTrack = styled.span<{ isActive: boolean }>(({ isActive }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: isActive ? themeColors.primary1 : themeColors.background6,
  borderRadius: '12px',
  transition: 'background-color 0.2s ease-in-out',
  cursor: 'pointer',
}));

const ToggleKnob = styled.span<{ isActive: boolean }>(({ isActive }) => ({
  position: 'absolute',
  top: '2px',
  left: isActive ? '22px' : '2px',
  width: '20px',
  height: '20px',
  backgroundColor: themeColors.white,
  borderRadius: '50%',
  transition: 'left 0.2s ease-in-out',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  pointerEvents: 'none',
}));

const InputLabelStyle = styled(InputLabel)({
  marginBottom: '4px',
});

const DescriptionText = styled.div({
  fontSize: '12px',
  color: themeColors.text4,
  lineHeight: '16px',
});

export type ToggleInputProps = {
  id?: string;
  label?: string;
  description?: string;
  value?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  error?: string;
  className?: string;
};

export const ToggleInput: React.FC<ToggleInputProps> = ({
  id,
  label,
  description,
  value = false,
  onChange,
  disabled = false,
  error = '',
  className,
}) => {
  const [isActive, setIsActive] = useState<boolean>(value);
  const generatedId = useId();
  const inputId = id || generatedId;
  const descriptionId = `${inputId}-description`;
  const errorId = errorIdFor(inputId);
  const describedBy = [description ? descriptionId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  useEffect(() => {
    setIsActive(value);
  }, [value]);

  const handleToggle = () => {
    if (disabled) return;
    const newValue = !isActive;
    setIsActive(newValue);
    if (onChange) onChange(newValue);
  };

  return (
    <Container className={className}>
      <TextContainer>
        {label && <InputLabelStyle htmlFor={inputId} disabled={disabled}>{label}</InputLabelStyle>}
        {description && <DescriptionText id={descriptionId}>{description}</DescriptionText>}
      </TextContainer>
      
      <ToggleContainer>
        <HiddenInput
          type="checkbox"
          role="switch"
          id={inputId}
          checked={isActive}
          onChange={handleToggle}
          onKeyDown={onEnterKey(handleToggle)}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
        <ToggleTrack isActive={isActive} />
        <ToggleKnob isActive={isActive} />
      </ToggleContainer>
      
      {error && <ValidationErrorStyle id={errorId}>{error}</ValidationErrorStyle>}
    </Container>
  );
};
